import React, { useState, useEffect } from "react";
import { Badge, Button, Text, useToast } from "@chakra-ui/react";
import dayjs from "dayjs";
import ReusableTable from "./ReusableTable";
import { getPaymentRecord } from "../pages/topUp/services/getPaymentRecord";

const RECORDS_PER_PAGE = 10;

export const PaymentRecordTable = ({
  userId,
  estateId,
  commodityType,
  onRepurchase,
}) => {
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (!userId) return;
    const skip = (currentPage - 1) * RECORDS_PER_PAGE;
    setLoading(true);
    getPaymentRecord(
      userId,
      setRecords,
      setLoading,
      estateId,
      { limit: RECORDS_PER_PAGE, skip, commodityType, onError: setLoadError },
      setTotalRecords,
    );
  }, [userId, estateId, commodityType, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [estateId, commodityType]);

  const columns = [
    { key: "commodityType", header: "Commodity" },
    {
      key: "createdAt",
      header: "Date",
      render: (row) => (
        <>
          <Text>{dayjs(row?.createdAt).format("DD MMM YYYY")}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Last Payment
          </Text>
        </>
      ),
    },
    {
      key: "meterId",
      header: "Meter",
      render: (row) => (
        <>
          <Text>{row?.meterId}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Meter Reading
          </Text>
        </>
      ),
    },
    {
      key: "unitPurchased",
      header: "Unit",
      render: (row) => (
        <>
          <Text>{row?.unitPurchased}</Text>
          <Text fontSize="12px" fontWeight="normal">
            {row?.unit}
          </Text>
        </>
      ),
    },
    {
      key: "statusLabel",
      header: "Status",
      render: (row) =>
        row?.statusLabel ? (
          <Badge
            colorScheme={
              row.underReview
                ? "orange"
                : row.statusLabel === "Completed"
                  ? "green"
                  : row.statusLabel === "Refunded"
                    ? "red"
                    : "blue"
            }
          >
            {row.underReview ? "Under review" : row.statusLabel}
          </Badge>
        ) : (
          <Text fontSize="11px" color="gray.400">—</Text>
        ),
    },
    {
      key: "token",
      header: "Token",
      render: (row) =>
        row?.token ? (
          <Button
            size="xs"
            variant="outline"
            maxW="140px"
            onClick={() =>
              navigator.clipboard.writeText(row.token).then(() =>
                toast({
                  title: "Token copied!",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                  position: "top",
                })
              )
            }
            title={row.token}
          >
            <Text isTruncated maxW="120px">
              {row.token}
            </Text>
          </Button>
        ) : (
          <Text fontSize="12px" color="gray.400">
            {row?.underReview ? (
              <Text fontSize="11px" color="orange.500" fontWeight="semibold">
                Under review
              </Text>
            ) : row?.status === "PENDING" || row?.status === "PROCESSING" ? (
              <Text fontSize="11px" color="orange.400" fontWeight="semibold">
                Awaiting token
              </Text>
            ) : row?.status === "FAILED" ? (
              <Text fontSize="11px" color="red.400" fontWeight="semibold">
                Failed
              </Text>
            ) : (
              <Text fontSize="11px" color="gray.400">—</Text>
            )}
          </Text>
        ),
    },
    {
      key: "amountPaid",
      header: "Amount",
      render: (row) => (
        <>
          <Text>{row?.amountPaid}</Text>
          <Text fontSize="12px" fontWeight="normal">
            Total Cost
          </Text>
        </>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Button
          bg="primary.500"
          fontWeight="normal"
          onClick={() => onRepurchase && onRepurchase(row)}
        >
          Re-Purchase
        </Button>
      ),
    },
  ];

  if (loadError && !loading) {
    return (
      <Text color="red.500" fontSize="14px" py="10px" role="alert">
        {loadError}
      </Text>
    );
  }

  return (
    <ReusableTable
      columns={columns}
      data={records}
      emptyText="No payment records found"
      loading={loading}
      enablePagination={true}
      currentPage={currentPage}
      totalPages={Math.ceil(totalRecords / RECORDS_PER_PAGE)}
      onPageChange={(page) => setCurrentPage(page)}
    />
  );
};
